import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";

export const UpdatePasswordForm = () => {
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);

    const { updateUserPassword } = useAuth();
    const navigate = useNavigate();

    const handleUpdatePassword = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword !== confirmNewPassword) return;

        setIsUpdatingPassword(true);
        // Primeiro, precisamos garantir que o user está atualizado para verificar o email
        // No entanto, updateUserPassword já atualiza o user no state do useAuth se der certo?
        // Como o hook useAuth pode não estar sincronizado instantaneamente, vamos tentar obter o usuário diretamente 
        // ou confiar que após o setSession ele está disponível.

        const { error } = await updateUserPassword(newPassword);

        if (!error) {
            // Tenta obter o usuário atual para verificação de email
            let currentUserEmail = "";
            try {
                // Tenta pegar o email do usuário na sessão atual
                const { data: { user } } = await import("@/lib/supabase").then(m => m.supabase.auth.getUser());
                currentUserEmail = user?.email || "";
            } catch (err) {
                console.error("Erro ao obter usuário para redirecionamento:", err);
            }

            console.log("🔒 Senha atualizada. Email verificado:", currentUserEmail);

            if (currentUserEmail.endsWith("@letswatch.com.br")) {
                console.log("🔒 Redirecionando para Painel Admin");
                navigate('/admin-dashboard');
            } else {
                console.log("🔒 Redirecionando para Dashboard");
                navigate('/dashboard');
            }
        }
        setIsUpdatingPassword(false);
    };

    return (
        <div className="w-full animate-in fade-in duration-500">
            <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-500/30">
                    <Lock className="h-8 w-8 text-green-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Redefinir Senha</h3>
                <p className="text-sm text-gray-500 mt-2">Crie uma nova senha segura para sua conta.</p>
            </div>

            <form onSubmit={handleUpdatePassword} className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="new-password">Nova Senha</Label>
                    <div className="relative group">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 group-focus-within:text-green-600 transition-colors" />
                        <Input
                            id="new-password"
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Mínimo 6 caracteres"
                            className="pl-10 pr-10 border-gray-200 focus:border-green-500 focus:ring-green-500 transition-all"
                            required
                            minLength={6}
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1 h-8 w-8 hover:bg-transparent"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                        >
                            {showNewPassword ? (
                                <EyeOff className="h-4 w-4 text-gray-400 hover:text-green-600" />
                            ) : (
                                <Eye className="h-4 w-4 text-gray-400 hover:text-green-600" />
                            )}
                        </Button>
                    </div>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="confirm-new-password">Confirmar Nova Senha</Label>
                    <div className="relative group">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400 group-focus-within:text-green-600 transition-colors" />
                        <Input
                            id="confirm-new-password"
                            type={showConfirmNewPassword ? "text" : "password"}
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            placeholder="Digite a senha novamente"
                            className="pl-10 pr-10 border-gray-200 focus:border-green-500 focus:ring-green-500 transition-all"
                            required
                            minLength={6}
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="absolute right-1 top-1 h-8 w-8 hover:bg-transparent"
                            onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                        >
                            {showConfirmNewPassword ? (
                                <EyeOff className="h-4 w-4 text-gray-400 hover:text-green-600" />
                            ) : (
                                <Eye className="h-4 w-4 text-gray-400 hover:text-green-600" />
                            )}
                        </Button>
                    </div>
                </div>

                {newPassword !== confirmNewPassword && confirmNewPassword && (
                    <div className="flex items-center gap-2 text-sm text-red-500 bg-red-50 p-2 rounded-md animate-in slide-in-from-top-1">
                        <span className="font-bold">⚠</span> As senhas não coincidem
                    </div>
                )}

                <Button
                    type="submit"
                    className="w-full h-11 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold shadow-lg shadow-green-900/20 transform transition-all active:scale-[0.98]"
                    disabled={isUpdatingPassword || newPassword !== confirmNewPassword}
                >
                    {isUpdatingPassword ? (
                        <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Salvando...
                        </>
                    ) : (
                        "Salvar Nova Senha"
                    )}
                </Button>
            </form>
        </div>
    );
};
